import { article as bibleInAYearOnYourPhone } from "./bible-in-a-year-on-your-phone";
import { article as brickAlternative } from "./brick-alternative";
import { article as christianAppBlockerGuide } from "./christian-app-blocker-guide";
import { article as digitalSabbath } from "./digital-sabbath";
import { article as dopamineDetoxChristian } from "./dopamine-detox-christian";
import { article as freedomAlternative } from "./freedom-alternative";
import { article as howToMemorizeBibleVerses } from "./how-to-memorize-bible-verses";
import { article as howToStopScrollingAtNight } from "./how-to-stop-scrolling-at-night";
import { article as jomoAlternative } from "./jomo-alternative";
import { article as morningQuietTimeRoutine } from "./morning-quiet-time-routine";
import { article as oneSecAlternative } from "./one-sec-alternative";
import { article as opalAlternative } from "./opal-alternative";
import { article as phoneAddictionBibleVerses } from "./phone-addiction-bible-verses";
import { article as phoneRulesForChristianFamilies } from "./phone-rules-for-christian-families";
import { article as screenTimeLimitsDontWork } from "./screen-time-limits-dont-work";
import { article as screenzenAlternative } from "./screenzen-alternative";
import { article as socialMediaFastGuide } from "./social-media-fast-guide";
import type { Article, Block } from "./types";

/**
 * Every article, newest first.
 *
 * ── Why this is a static barrel and the landing copy is not ──────────────────
 *
 * `lib/marketing` loads one language per request because eight of the nine
 * bundles are dead weight to any given visitor. Articles are the opposite: the
 * index page needs all of their titles, the sitemap needs all of their dates,
 * and the whole corpus is smaller than one of the screenshots. Splitting it
 * would cost a request to save nothing.
 *
 * That stops being true somewhere north of thirty articles. The seam is here:
 * when the content chunk starts showing up in the bundle report, give the index
 * page a summary list and move the bodies behind dynamic imports, exactly as
 * `marketing/registry.ts` does.
 *
 * ── The order is the order they are listed in ────────────────────────────────
 *
 * Hand-written rather than sorted by date, because publication date is a poor
 * proxy for what a first-time reader should be handed. Sorting by date makes
 * the newest thing the front door whether or not it deserves to be.
 */
export const ARTICLES: Article[] = [
  screenTimeLimitsDontWork,
  howToStopScrollingAtNight,
  christianAppBlockerGuide,
  opalAlternative,
  oneSecAlternative,
  freedomAlternative,
  screenzenAlternative,
  brickAlternative,
  jomoAlternative,
  socialMediaFastGuide,
  dopamineDetoxChristian,
  morningQuietTimeRoutine,
  digitalSabbath,
  bibleInAYearOnYourPhone,
  howToMemorizeBibleVerses,
  phoneAddictionBibleVerses,
  phoneRulesForChristianFamilies,
];

export const ARTICLES_BY_SLUG: Record<string, Article> = Object.fromEntries(
  ARTICLES.map((a) => [a.slug, a]),
);

export function articleBySlug(slug: string): Article | undefined {
  return ARTICLES_BY_SLUG[slug];
}

/** The path an article lives at. One place, so the sitemap cannot drift. */
export function articlePath(slug: string): string {
  return `/blog/${slug}`;
}

/**
 * Roughly how long the article takes to read, in minutes.
 *
 * Printed under the title because it is the single most requested piece of
 * information on any long page, and because a reader who knows it is eight
 * minutes is a reader who does not bounce at paragraph two to go and check.
 *
 * 220 words a minute is the usual figure for adult silent reading of ordinary
 * prose. Rounded up, and never below one.
 */
export function readingMinutes(article: Article): number {
  const words = article.body.reduce((n, block) => n + blockWords(block), 0);
  return Math.max(1, Math.round(words / 220));
}

function blockWords(block: Block): number {
  switch (block.type) {
    case "ul":
    case "ol":
      return block.items.join(" ").split(/\s+/).length;
    case "verse":
      return block.verse.text.split(/\s+/).length;
    default:
      return block.text.split(/\s+/).length;
  }
}

/** The `<h2>`s of an article, for its table of contents. */
export function outline(article: Article): { id: string; text: string }[] {
  return article.body.flatMap((block) =>
    block.type === "h2" ? [{ id: block.id, text: block.text }] : [],
  );
}

export type { Article, ArticleFaq, Block, Links, Verse } from "./types";
